import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';

/**
 * Story 30.10 — legacy `/categories?id=X` → `/c/X` redirect.
 *
 * The Category-Hub (`/c/:slug`, Story 30.4) is now the canonical per-category
 * destination, and its resolver accepts an id OR a slug — so the old numeric
 * `?id` URLs redirect straight to `/c/{id}`. `country_id` is preserved (it maps
 * to the Hub's `?country_id` facet); the obsolete `name`/`hasSub` params are
 * dropped (the Hub resolves the category from the ref, it doesn't need them).
 *
 * Bare `/categories` (no `?id`) is deliberately NOT redirected — it keeps
 * rendering the legacy `CategoryResultsComponent` as the interim landing until
 * the All-Categories index page ships (Story 30-10b). The guard returns `true`
 * for that case so the route's component loads normally.
 *
 * This is the client-side interim only. The SEO-correct server-side HTTP 301
 * is Epic 55's (it preserves link equity) — same "pull a slice of SEO forward"
 * pattern Story 30.1 used for the slug column. A guard-returned `UrlTree`
 * performs a redirect that replaces the un-committed `/categories?id=X` history
 * entry (effectively `replaceUrl: true`) and is SSR-safe.
 */
export const legacyCategoryRedirectGuard: CanActivateFn = (route): boolean | UrlTree => {
  const id = route.queryParamMap.get('id');

  // Bare `/categories` (or a blank `?id`) → render the legacy listing (interim).
  if (id === null || id.trim() === '') {
    return true;
  }

  const router = inject(Router);
  const countryId = route.queryParamMap.get('country_id');

  return router.createUrlTree(['/c', id.trim()], {
    queryParams: countryId ? { country_id: countryId } : undefined,
  });
};
